/*
  Product customization inputs (custom text / custom image).
  - Live character counter for the custom text field
  - Image crop modal (fixed frame from the product's data-crop-ratio
    metafield, drag to pan, pinch/slider to zoom) BEFORE add to cart
  - Image preview + 5MB / type validation via setCustomValidity
  - Delegates sticky-bar add-to-cart to the MAIN product form so the
    custom line item properties are always included
*/
(function () {
  function init() {
    var wrapper = document.querySelector('[data-custom-inputs-wrapper]');
    if (!wrapper) return;

    // --- custom text fields: live counters (field 1 and optional field 2) ---
    [
      ['[data-custom-text]', '[data-custom-text-counter]'],
      ['[data-custom-text-2]', '[data-custom-text-2-counter]']
    ].forEach(function (pair) {
      var input = wrapper.querySelector(pair[0]);
      var counter = wrapper.querySelector(pair[1]);
      if (input && counter) {
        var max = input.getAttribute('maxlength') || '';
        var update = function () {
          counter.textContent = input.value.length + '/' + max;
        };
        input.addEventListener('input', update);
        update();
      }
    });

    // --- variant-driven visibility (all custom input groups) ---
    // data-variant-filter holds comma-separated variant IDs and/or
    // variant-title keywords; blank means "show for every variant".
    var filterGroups = Array.prototype.filter.call(
      wrapper.querySelectorAll('[data-variant-filter]'),
      function (group) { return (group.dataset.variantFilter || '').trim() !== ''; }
    );
    if (filterGroups.length && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      var groupTokens = filterGroups.map(function (group) {
        return (group.dataset.variantFilter || '')
          .split(',')
          .map(function (t) { return t.trim().toLowerCase(); })
          .filter(Boolean);
      });

      var variantMatches = function (tokens, variant) {
        if (!tokens.length) return true;
        if (!variant) return false;
        var id = String(variant.id);
        var title = (variant.title || '').toLowerCase();
        return tokens.some(function (t) {
          return t === id || (title && title.indexOf(t) !== -1);
        });
      };

      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data && event.data.variant;
        filterGroups.forEach(function (group, i) {
          var show = variantMatches(groupTokens[i], variant);
          group.hidden = !show;
          Array.prototype.forEach.call(group.querySelectorAll('[data-custom-input]'), function (input) {
            input.disabled = !show; // keeps value, skips submit/validation when hidden
          });
        });
      });
    }

    // --- alphabet letter pickers (single-select A–Z) ---
    Array.prototype.forEach.call(
      wrapper.querySelectorAll('[data-custom-letter-group]'),
      setupLetterPicker
    );

    // --- custom images: crop modal + preview + validation ---
    // one group per upload field (image 1 and optional image 2), each
    // with its own dropzone, preview, error element and crop modal
    Array.prototype.forEach.call(
      wrapper.querySelectorAll('[data-custom-image-group], [data-custom-image-2-group]'),
      setupImageGroup
    );

    // NOTE: sticky-bar delegation moved into product-form.js
    // onSubmitHandler - its listener registers first, so delegating
    // from here always lost the race and the item was added without
    // validation.
  }

  function setupImageGroup(group) {
    var fileInput = group.querySelector('[data-custom-image]');
    var preview = group.querySelector('[data-custom-image-preview]');
    var errorEl = group.querySelector('[data-custom-image-error]');
    var zone = group.querySelector('[data-custom-image-zone]');

    if (fileInput) {
      var maxSize = parseInt(fileInput.dataset.maxSize, 10) || 5242880;
      var cropper = buildCropper(group, fileInput);

      var showError = function (msg) {
        fileInput.value = '';
        fileInput.setCustomValidity(msg);
        if (errorEl) {
          errorEl.textContent = msg;
          errorEl.classList.remove('hidden');
        }
        if (zone) zone.classList.add('product-custom-inputs--invalid');
      };

      var clearError = function () {
        fileInput.setCustomValidity('');
        if (errorEl) {
          errorEl.classList.add('hidden');
          errorEl.textContent = '';
        }
        if (zone) zone.classList.remove('product-custom-inputs--invalid');
      };

      var showPreview = function (file) {
        if (preview && window.URL && URL.createObjectURL) {
          preview.src = URL.createObjectURL(file);
          preview.classList.remove('hidden');
        }
      };

      fileInput.addEventListener('change', function () {
        clearError();
        if (preview) {
          preview.classList.add('hidden');
          preview.removeAttribute('src');
        }

        var file = fileInput.files && fileInput.files[0];
        if (!file) return;

        if (file.size > maxSize) {
          showError('Image is too large (max 5MB). Please choose a smaller photo.');
          return;
        }
        if (file.type && file.type.indexOf('image/') !== 0) {
          showError('Please upload an image file (JPG, PNG or HEIC).');
          return;
        }

        // already-cropped file re-entering via our own dispatch
        if (fileInput.dataset.skipCrop === '1') {
          delete fileInput.dataset.skipCrop;
          showPreview(file);
          return;
        }

        // open crop modal; if the browser can't decode (e.g. HEIC on
        // Chrome), fall back to using the original file as-is
        if (cropper) {
          cropper.open(file, function onUncroppable() {
            showPreview(file);
          });
        } else {
          showPreview(file);
        }
      });
    }
  }

  /* ---------------- alphabet letter picker ---------------- */
  // Horizontal A–Z buttons that fill a required (hidden-from-tab) text
  // input = the line item property + native validation anchor used by
  // product-form.js reportValidity(). Single-select.
  function setupLetterPicker(group) {
    var picker = group.querySelector('[data-letter-picker]');
    var valueInput = group.querySelector('[data-custom-letter]');
    if (!picker || !valueInput) return;
    var buttons = Array.prototype.slice.call(picker.querySelectorAll('.pci-letter'));
    var errorEl = group.querySelector('[data-letter-error]');

    var syncValidity = function () {
      valueInput.setCustomValidity(valueInput.value ? '' : 'Please choose a letter (A–Z).');
    };

    var clearError = function () {
      picker.classList.remove('pci-letters--invalid');
      if (errorEl) {
        errorEl.classList.add('hidden');
        errorEl.textContent = '';
      }
    };

    // reportValidity() (product-form.js) can't show a native bubble on the
    // hidden value input, so surface the message inline on the picker.
    valueInput.addEventListener('invalid', function () {
      picker.classList.add('pci-letters--invalid');
      if (errorEl) {
        errorEl.textContent = valueInput.validationMessage || 'Please choose a letter (A–Z).';
        errorEl.classList.remove('hidden');
      }
    });

    var select = function (btn) {
      var ch = btn.getAttribute('data-letter') || '';
      buttons.forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('pci-letter--active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      valueInput.value = ch;
      syncValidity();
      clearError();
    };

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () { select(btn); });
    });

    // pre-select if a value is already set (restored state), else init validity
    var pre = valueInput.value && buttons.filter(function (b) {
      return b.getAttribute('data-letter') === valueInput.value;
    })[0];
    if (pre) select(pre);
    else syncValidity();
  }

  /* ---------------- crop modal engine ---------------- */
  function buildCropper(group, fileInput) {
    var modal = group.querySelector('[data-crop-modal]');
    if (!modal || !window.DataTransfer) return null;

    // Portal: the modal is rendered inside the product form, whose
    // ancestors have transforms/animations that break position:fixed
    // (it becomes relative to the transformed ancestor and gets
    // clipped). Re-parent to <body> so it truly overlays the viewport.
    document.body.appendChild(modal);

    var frame = modal.querySelector('[data-crop-frame]');
    var img = modal.querySelector('[data-crop-img]');
    var zoomSlider = modal.querySelector('[data-crop-zoom]');
    var btnCancel = modal.querySelector('[data-crop-cancel]');
    var btnConfirm = modal.querySelector('[data-crop-confirm]');

    // ratio from metafield - accepts "1:1", "4:3", "2000x2000", "16/9",
    // or a single number; anything unparseable falls back to square
    var ratio = 1;
    var rw = 1;
    var rh = 1;
    var raw = (fileInput.dataset.cropRatio || '').trim();
    var m = raw.match(/^(\d+(?:\.\d+)?)\s*[:xX\/]\s*(\d+(?:\.\d+)?)$/);
    if (m) {
      rw = parseFloat(m[1]);
      rh = parseFloat(m[2]);
    } else if (/^\d+(?:\.\d+)?$/.test(raw) && parseFloat(raw) > 0) {
      rw = parseFloat(raw);
      rh = 1;
    }
    if (!(rw > 0) || !(rh > 0)) {
      rw = 1;
      rh = 1;
    }
    ratio = rw / rh;
    frame.style.aspectRatio = rw + ' / ' + rh;

    // fallback if aspect-ratio is unsupported/ignored: set explicit height
    function ensureFrameHeight() {
      var r = frame.getBoundingClientRect();
      if (r.height < 10 && r.width > 0) {
        frame.style.height = Math.round(r.width / ratio) + 'px';
      }
    }

    var state = null; // { file, natW, natH, baseScale, zoom, tx, ty, objectUrl, onFail }

    function frameSize() {
      var r = frame.getBoundingClientRect();
      return { w: r.width, h: r.height };
    }

    function clampPan() {
      var f = frameSize();
      var s = state.baseScale * state.zoom;
      var w = state.natW * s;
      var h = state.natH * s;
      state.tx = Math.min(0, Math.max(f.w - w, state.tx));
      state.ty = Math.min(0, Math.max(f.h - h, state.ty));
    }

    function render() {
      var s = state.baseScale * state.zoom;
      img.style.transform = 'translate(' + state.tx + 'px,' + state.ty + 'px) scale(' + s + ')';
    }

    function setZoom(z, cx, cy) {
      var f = frameSize();
      cx = cx == null ? f.w / 2 : cx;
      cy = cy == null ? f.h / 2 : cy;
      var sOld = state.baseScale * state.zoom;
      state.zoom = Math.min(4, Math.max(1, z));
      var sNew = state.baseScale * state.zoom;
      // keep (cx, cy) stable while zooming
      state.tx = cx - ((cx - state.tx) / sOld) * sNew;
      state.ty = cy - ((cy - state.ty) / sOld) * sNew;
      clampPan();
      render();
      zoomSlider.value = Math.round(state.zoom * 100);
    }

    function open(file, onFail) {
      var url = URL.createObjectURL(file);
      var probe = new Image();
      probe.onload = function () {
        state = {
          file: file,
          natW: probe.naturalWidth,
          natH: probe.naturalHeight,
          zoom: 1,
          tx: 0,
          ty: 0,
          objectUrl: url,
          onFail: onFail
        };
        img.src = url;
        modal.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        // cover-fit base scale (needs frame to be laid out first)
        requestAnimationFrame(function () {
          ensureFrameHeight();
          var f = frameSize();
          state.baseScale = Math.max(f.w / state.natW, f.h / state.natH);
          // center
          state.tx = (f.w - state.natW * state.baseScale) / 2;
          state.ty = (f.h - state.natH * state.baseScale) / 2;
          zoomSlider.value = 100;
          render();
        });
      };
      probe.onerror = function () {
        // undecodable (HEIC on non-Safari) - keep original file
        URL.revokeObjectURL(url);
        if (onFail) onFail();
      };
      probe.src = url;
    }

    function close() {
      modal.classList.add('hidden');
      document.body.style.overflow = '';
      if (state && state.objectUrl) URL.revokeObjectURL(state.objectUrl);
      img.removeAttribute('src');
      state = null;
    }

    // --- pointer interactions: drag + pinch ---
    var pointers = {};
    var lastPinchDist = 0;

    frame.addEventListener('pointerdown', function (e) {
      if (!state) return;
      e.preventDefault();
      frame.setPointerCapture(e.pointerId);
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
    });

    frame.addEventListener('pointermove', function (e) {
      if (!state || !pointers[e.pointerId]) return;
      e.preventDefault();
      var ids = Object.keys(pointers);
      if (ids.length === 1) {
        state.tx += e.clientX - pointers[e.pointerId].x;
        state.ty += e.clientY - pointers[e.pointerId].y;
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        clampPan();
        render();
      } else if (ids.length >= 2) {
        pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var a = pointers[ids[0]];
        var b = pointers[ids[1]];
        var dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (lastPinchDist) {
          var fr = frame.getBoundingClientRect();
          var cx = (a.x + b.x) / 2 - fr.left;
          var cy = (a.y + b.y) / 2 - fr.top;
          setZoom(state.zoom * (dist / lastPinchDist), cx, cy);
        }
        lastPinchDist = dist;
      }
    });

    function endPointer(e) {
      delete pointers[e.pointerId];
      if (Object.keys(pointers).length < 2) lastPinchDist = 0;
    }
    frame.addEventListener('pointerup', endPointer);
    frame.addEventListener('pointercancel', endPointer);

    frame.addEventListener(
      'wheel',
      function (e) {
        if (!state) return;
        e.preventDefault();
        var fr = frame.getBoundingClientRect();
        setZoom(state.zoom * (e.deltaY < 0 ? 1.08 : 0.92), e.clientX - fr.left, e.clientY - fr.top);
      },
      { passive: false }
    );

    zoomSlider.addEventListener('input', function () {
      if (!state) return;
      setZoom(parseInt(zoomSlider.value, 10) / 100, null, null);
    });

    btnCancel.addEventListener('click', function () {
      fileInput.value = '';
      close();
    });

    btnConfirm.addEventListener('click', function () {
      if (!state) return;
      var f = frameSize();
      var s = state.baseScale * state.zoom;
      var sx = -state.tx / s;
      var sy = -state.ty / s;
      var sw = f.w / s;
      var sh = f.h / s;

      // cap output at 1600px on the long edge
      var outW = Math.min(1600, Math.round(sw));
      var outH = Math.round(outW / ratio);

      var canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      var ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

      var fallback = state.onFail;
      var origName = (state.file.name || 'image').replace(/\.[^.]+$/, '');
      canvas.toBlob(
        function (blob) {
          if (!blob) {
            close();
            if (fallback) fallback();
            return;
          }
          var cropped = new File([blob], origName + '-cropped.jpg', { type: 'image/jpeg' });
          var dt = new DataTransfer();
          dt.items.add(cropped);
          fileInput.dataset.skipCrop = '1';
          fileInput.files = dt.files;
          close();
          fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        },
        'image/jpeg',
        0.9
      );
    });

    return { open: open };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
